import { ReactElement } from 'react';
import { Flex, Box, Image, BoxProps, Button } from '@chakra-ui/react';
import { useLocation, Link } from 'react-router-dom';
import { IndexRoutes, ProtectedRoutes, PublicRoutes, RouteType } from 'types/Routes';
import { useCurrentRoute } from 'hooks/utils/useCurrentRoute';
import { ExtendedLogo } from 'assets/common/ExtendedLogo';
import Background from 'assets/common/background.png';
import { ErrorBoundary } from 'react-error-boundary';
import { ModalTarget, ModalVariant } from 'types/Modal';
import { Z_INDEX } from 'styles/zIndex';
import { renderError } from 'components/common/ErrorFallback';
import { ShareIOSIcon } from 'assets/icons/ShareIOSIcon';
import { useModalService } from 'hooks/useModalService';
import { useTopBarContext } from 'hooks/utils/useTopBarContext';
import { NavigationBarDesktop } from '../NavigationBar/NavigationBarDesktop/NavigationBarDesktop';
import { SearchBar } from '../../Search/SearchBar';
import { Breadcrumbs } from '../Breadcrumbs/Breadcrumbs';
import { useUser } from '../../../utils/auth';
import { AdminBar } from '../TopBar/AdminBar';
import { TopBarWorksheetSession } from '../TopBar/TopBarWorksheetSession';
import { TopBarWorksheetEditor } from '../TopBar/TopBarWorksheetEditor';

type Props = {
  children: ReactElement;
};

export const DesktopLayout = ({ children }: Props): ReactElement => {
  const location = useLocation();
  const user = useUser();
  const { routeType, currentRoute } = useCurrentRoute(location);
  const { title, customOnBackClick, worksheetEditorOptions, worksheetSessionOptions } =
    useTopBarContext().currentTopBarValues;
  const { show: showModal } = useModalService();
  const isInApp = routeType === RouteType.PROTECTED;
  const isNotDefinedRoute = routeType === RouteType.NOT_DEFINED; // 404 page
  const showFullPage = [
    ProtectedRoutes.ORG_MANAGEMENT_USER_DASHBOARD,
    ProtectedRoutes.ORG_MANAGEMENT_VOUCHER_DASHBOARD,
    ProtectedRoutes.ORG_MANAGEMENT_CLASS_DASHBOARD,
    ProtectedRoutes.ORG_MANAGEMENT_CLASS_DETAIL,
  ].includes(currentRoute as ProtectedRoutes);

  const isWorksheetSession = currentRoute === ProtectedRoutes.WORKSHEET_SESSION;
  const isWorksheetEditor =
    location.pathname.startsWith(`${ProtectedRoutes.WORKSHEET_DETAIL}/`) &&
    !location.pathname.startsWith(`${ProtectedRoutes.WORKSHEET_DETAIL}/class`);

  const containerPaddingX = (isInApp || isNotDefinedRoute) && !showFullPage ? '80px' : '0px';

  const handleShare = (): void => {
    showModal(ModalVariant.SHARE);
  };

  const renderNavigationBarDesktop = (): ReactElement | null => {
    if ((isInApp || (isNotDefinedRoute && user.data)) && !isWorksheetSession) {
      return <NavigationBarDesktop />;
    }
    return null;
  };

  const renderSearchBar = (): ReactElement => (
    <Box
      width="100%"
      backgroundColor="background.primary"
      h="70px"
      position="sticky"
      top="0"
      borderBottom="default"
      borderBottomColor="border.accent"
      zIndex={Z_INDEX.SEARCH_BAR}
    >
      <SearchBar />
      <Button
        variant="primary"
        size="m"
        position="absolute"
        right="l"
        top="s"
        onClick={handleShare}
      >
        <ShareIOSIcon />
        Freund:in einladen
      </Button>
    </Box>
  );

  const renderMainContent = (): ReactElement => (
    <Flex height="100%" flexDirection="column">
      <Breadcrumbs />
      {!isInApp && !isNotDefinedRoute && (
        <Box pt="50px">
          <Link to="/">
            <ExtendedLogo w="124px" h="70px" opacity="1" />
          </Link>
        </Box>
      )}
      <Box
        flexGrow={1}
        display="flex"
        flexDir="column"
        justifyContent={isInApp ? undefined : 'center'}
      >
        <ErrorBoundary fallbackRender={renderError}>{children}</ErrorBoundary>
      </Box>
    </Flex>
  );

  // Render the image only if the user is not logged in and make use of the grid layout
  const renderLoginImage = (): ReactElement => (
    <Box bg="#EAFDF5" height="100%" display="flex" justifyContent="center">
      <Image src={Background} alt="background" my="auto" />
    </Box>
  );

  const getContainerWidth = (): BoxProps['maxW'] => {
    if (currentRoute === ProtectedRoutes.WORKSHEET_DETAIL) {
      return '1920px';
    }
    return '1120px';
  };

  return (
    <>
      <Flex flexGrow="1" position="relative" flexDirection="column">
        <AdminBar isInApp={isInApp || currentRoute === IndexRoutes.FIRST_TIME} />
      </Flex>
      <Flex minH="100vh" background="background.canvas" position="relative">
        {renderNavigationBarDesktop()}

        <Flex
          flexGrow="1"
          position="relative"
          id={ModalTarget.PAGE_DESKTOP}
          flexDirection="column"
          minWidth="0"
        >
          {isInApp && !isWorksheetSession && !isWorksheetEditor && renderSearchBar()}
          {isInApp && isWorksheetSession && (
            <TopBarWorksheetSession
              title={title}
              onBackClick={customOnBackClick}
              ButtonComponents={worksheetSessionOptions?.ButtonComponents}
            />
          )}
          {isInApp && isWorksheetEditor && worksheetEditorOptions && (
            <TopBarWorksheetEditor
              isViewMode={worksheetEditorOptions.isViewMode}
              title={worksheetEditorOptions.title}
              ButtonComponents={worksheetEditorOptions.ButtonComponents}
            />
          )}
          <Box
            maxW={getContainerWidth()}
            width="100%"
            margin="0 auto"
            paddingX={containerPaddingX}
            paddingTop="0"
            paddingBottom={isInApp ? '80px' : '0'}
            // padding={isInApp ? `0px 80px 80px` : `0px 80px`}
            display={isInApp || isNotDefinedRoute ? 'flex' : 'grid'}
            flexDirection={isInApp ? 'column' : undefined}
            flexGrow={isInApp ? 1 : undefined}
            gridTemplateColumns={isInApp ? undefined : '1fr 1fr'}
            minH={isInApp ? undefined : '100vh'}
            position={isInApp ? undefined : 'relative'}
            alignItems={isInApp ? undefined : 'center'}
            gridGap={isInApp ? undefined : 'l'}
          >
            {renderMainContent()}
            {!isInApp && !isNotDefinedRoute && renderLoginImage()}
          </Box>
        </Flex>
      </Flex>
    </>
  );
};
